/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AutocompleteRenderInputParams, FormControl, InputLabel, OutlinedInput } from '@mui/material';

import { ArrowRoundedIcon } from '../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../i18n';
import { useFormStyles } from '../../../styles/form';
import { ClusterAllocationMode } from '../../../types/common/AllocationMode';
import {
  CreateClusterBackupSettings,
  CreateClusterSettingsStep,
  CreateClusterValues,
} from '../CreateClusterOverlay.types';
import { DynamicAllocation } from './forms/DynamicAllocation';
import { Replicas } from './forms/Replicas';
import { TShirtSizes } from './forms/TShirtSizes';

export const CreateClusterStepSettings = memo(
  <
    Values extends CreateClusterValues &
      Partial<CreateClusterBackupSettings> & {
        suggestedPgConfigParameters?: Record<string, unknown>;
      },
  >(
    props: CreateClusterSettingsStep<Values>,
  ) => {
    const { values, setFieldValue, validateForm, mode, postgresVersions, serviceType } = props;

    const formClasses = useFormStyles();

    const { t } = useTranslation(Namespaces.Admin);

    useEffect(() => {
      void validateForm();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // const handleVersionChange = async (_e: unknown, value: number | null) =>
    //   value && (await setFieldValue('postgresVersion', value));

    const handleVersionChange = useCallback(
      (value: number | null) => value && setFieldValue('postgresVersion', value),
      [setFieldValue],
    );

    const renderAutocompleteInput = (renderParams: AutocompleteRenderInputParams) => (
      <OutlinedInput
        {...destructRenderInputParams(renderParams)}
        id="postgresVersion"
        name="postgresVersion"
        label={t('postgres_version.label')}
        aria-disabled={!postgresVersions.length}
      />
    );

    useEffect(() => {
      if (
        !!values.backupClusterConfiguration &&
        values.backupClusterConfiguration.postgresVersion !== values.postgresVersion
      ) {
        void handleVersionChange(values.backupClusterConfiguration.postgresVersion);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.backupClusterConfiguration, handleVersionChange]);

    const dialogs = {
      [ClusterAllocationMode.Dynamic]: <DynamicAllocation {...(props as any)} />,
      [ClusterAllocationMode.Plan]: <TShirtSizes {...(props as any)} />,
    } as const;

    const replicasProps = {
      values,
      setFieldValue,
    } as const;

    const getOptionLabel = (value: number) => String(value);

    const isPostgres = useMemo(() => serviceType !== 'cypex' && serviceType !== 'migrator', [serviceType]);

    return (
      <>
        {mode && dialogs[mode]}
        {isPostgres && (
          <>
            <FormControl className={cx(formClasses.formControl, formClasses.widthDefault)} variant="outlined">
              <InputLabel htmlFor="postgresVersion">{t('postgres_version.label')}</InputLabel>
              <HUAutocomplete
                renderInput={renderAutocompleteInput}
                popupIcon={<ArrowRoundedIcon />}
                options={postgresVersions}
                disabled={!!values.backupClusterConfiguration || !postgresVersions.length}
                disableClearable
                value={values.postgresVersion}
                onChange={(_e, value) => handleVersionChange(value)}
                getOptionLabel={getOptionLabel}
                data-cy="create-cluster-postgres-version-field"
              />
            </FormControl>
            {serviceType !== 'babelfish' && <Replicas {...replicasProps} />}
          </>
        )}
      </>
    );
  },
);
